import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useIntent, STAGE_CONFIG, TRACK_COPY } from "@/context/IntentContext";
import { useAuth } from "@/context/AuthContext";
import { apiPost } from "@/api/client";

const STAGE_LABELS: Record<string, string> = {
  previous: "Previous Season",
  upcoming: "Upcoming Season",
  midseason: "Mid-Season",
};

const TRACK_LABELS: Record<string, string> = {
  profit: "Maximise Profit",
  seats: "Fill Every Seat",
  brand: "Build My Brand",
};

export function FringeGetStartedPage() {
  const navigate = useNavigate();
  const { state, setState } = useIntent();
  const { refreshSession } = useAuth();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [focusField, setFocusField] = useState<string | null>(null);
  const [hoveredSubmit, setHoveredSubmit] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const stageColor = state.stage ? STAGE_CONFIG[state.stage].color : "#3d8bd4";
  const stageColorRgb = state.stage ? STAGE_CONFIG[state.stage].colorRgb : "61, 139, 212";

  const handleFirstNameChange = (val: string) => {
    setState((prev) => ({ ...prev, firstName: val }));
  };

  const handleEmailChange = (val: string) => {
    setState((prev) => ({ ...prev, email: val }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setSubmitting(true);
    try {
      await apiPost<{ user: any }>("/api/signup", {
        email: state.email,
        password,
        display_name: state.firstName,
        promoCode: state.promoCode || "NEWBIE",
        stage: state.stage,
        track: state.track,
      });

      // Signup auto-logs in -- refresh session to pick up the new user
      await refreshSession();
      navigate("/login?registered=true");
    } catch (err: any) {
      setError(err?.message || "Signup failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = (field: string): React.CSSProperties => ({
    width: "100%",
    padding: "14px 18px",
    background: "rgba(18, 18, 28, 0.8)",
    border: `1.5px solid ${focusField === field ? `rgba(${stageColorRgb}, 0.6)` : "rgba(255, 255, 255, 0.1)"}`,
    borderRadius: "10px",
    color: "#fafafc",
    fontSize: "15px",
    outline: "none",
    boxShadow: focusField === field
      ? `0 0 0 3px rgba(${stageColorRgb}, 0.15), 0 0 20px rgba(${stageColorRgb}, 0.1)`
      : "none",
    transition: "border-color 0.25s ease, box-shadow 0.25s ease",
    boxSizing: "border-box" as const,
  });

  const labelStyle: React.CSSProperties = {
    display: "block",
    color: "#b0b0c0",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.12em",
    marginBottom: "8px",
    fontWeight: 600,
  };

  return (
    <div style={{
      background: "#0a0608",
      minHeight: "100dvh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
      boxSizing: "border-box",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Background curtain images */}
      <img src="/frontassets/front_curtain_left.jpg" alt="" style={{
        position: "absolute", top: 0, left: 0, width: "50%", height: "100%",
        objectFit: "cover", objectPosition: "right center", pointerEvents: "none",
        opacity: 0.4,
      }} />
      <img src="/frontassets/front_curtain_right.jpg" alt="" style={{
        position: "absolute", top: 0, right: 0, width: "50%", height: "100%",
        objectFit: "cover", objectPosition: "left center", pointerEvents: "none",
        opacity: 0.4,
      }} />
      <div style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        background: "radial-gradient(ellipse at center, transparent 20%, #0a0608 75%)",
      }} />

      <div style={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: "460px",
        background: "rgba(18, 18, 28, 0.92)",
        backdropFilter: "blur(16px)",
        borderRadius: "14px",
        padding: "clamp(28px, 5vw, 48px)",
        border: `1.5px solid rgba(${stageColorRgb}, 0.3)`,
        boxShadow: `0 0 20px rgba(${stageColorRgb}, 0.15), 0 0 60px rgba(${stageColorRgb}, 0.06), 0 8px 40px rgba(0,0,0,0.5)`,
        boxSizing: "border-box",
      }}>
        {/* Back link */}
        <Link
          to="/fringe"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            color: "#808094",
            fontSize: "12px",
            textDecoration: "none",
            marginBottom: "16px",
            letterSpacing: "0.05em",
            transition: "color 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = stageColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = "#808094")}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </Link>

        <h1 style={{
          color: "#fafafc",
          fontSize: "clamp(22px, 4vw, 32px)",
          fontWeight: 800,
          margin: "0 0 8px 0",
          textAlign: "center",
          letterSpacing: "0.02em",
          textTransform: "uppercase",
          textShadow: "0 0 20px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.7)",
        }}>
          Get Started
        </h1>

        <div style={{
          width: "60px",
          height: "2px",
          background: `linear-gradient(90deg, transparent, rgba(${stageColorRgb}, 0.6), transparent)`,
          margin: "0 auto 16px",
        }} />

        {/* Stage + Track summary */}
        {(state.stage || state.track) && (
          <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "center",
            marginBottom: "clamp(16px, 3%, 24px)",
          }}>
            {state.stage && (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 14px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.04em",
                color: STAGE_CONFIG[state.stage].color,
                background: `rgba(${STAGE_CONFIG[state.stage].colorRgb}, 0.12)`,
                border: `1px solid rgba(${STAGE_CONFIG[state.stage].colorRgb}, 0.25)`,
              }}>
                {STAGE_LABELS[state.stage] || state.stage}
              </span>
            )}
            {state.track && (
              <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 14px",
                borderRadius: "20px",
                fontSize: "12px",
                fontWeight: 600,
                letterSpacing: "0.04em",
                color: "#fafafc",
                background: "rgba(255, 255, 255, 0.06)",
                border: "1px solid rgba(255, 255, 255, 0.12)",
              }}>
                {TRACK_COPY[state.track].icon} {TRACK_LABELS[state.track] || state.track}
              </span>
            )}
          </div>
        )}

        {/* Error message */}
        {error && (
          <div style={{
            padding: "10px 14px",
            marginBottom: "16px",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#f87171",
            background: "rgba(248, 113, 113, 0.08)",
            border: "1px solid rgba(248, 113, 113, 0.2)",
            textAlign: "center",
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div>
            <label style={labelStyle}>First Name</label>
            <input
              type="text"
              required
              value={state.firstName}
              onChange={(e) => handleFirstNameChange(e.target.value)}
              onFocus={() => setFocusField("firstName")}
              onBlur={() => setFocusField(null)}
              placeholder="Your first name"
              style={inputStyle("firstName")}
              disabled={submitting}
            />
          </div>

          <div>
            <label style={labelStyle}>Email Address</label>
            <input
              type="email"
              required
              value={state.email}
              onChange={(e) => handleEmailChange(e.target.value)}
              onFocus={() => setFocusField("email")}
              onBlur={() => setFocusField(null)}
              placeholder="name@example.com"
              style={inputStyle("email")}
              disabled={submitting}
            />
          </div>

          <div>
            <label style={labelStyle}>Password</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocusField("password")}
              onBlur={() => setFocusField(null)}
              placeholder="Min. 8 characters"
              style={inputStyle("password")}
              disabled={submitting}
              minLength={8}
            />
          </div>

          <div>
            <label style={labelStyle}>Confirm Password</label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={() => setFocusField("confirmPassword")}
              onBlur={() => setFocusField(null)}
              placeholder="Repeat your password"
              style={inputStyle("confirmPassword")}
              disabled={submitting}
              minLength={8}
            />
          </div>

          {/* Promo code (optional) */}
          <div>
            <label style={labelStyle}>
              Promo Code <span style={{ color: "#606070", fontWeight: 400, textTransform: "none" }}>(optional)</span>
            </label>
            <input
              type="text"
              value={state.promoCode}
              onChange={(e) => setState((prev) => ({ ...prev, promoCode: e.target.value }))}
              onFocus={() => setFocusField("promoCode")}
              onBlur={() => setFocusField(null)}
              placeholder="NEWBIE"
              style={inputStyle("promoCode")}
              disabled={submitting}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            onMouseEnter={() => setHoveredSubmit(true)}
            onMouseLeave={() => setHoveredSubmit(false)}
            style={{
              width: "100%",
              padding: "14px",
              marginTop: "4px",
              background: submitting ? `rgba(${stageColorRgb}, 0.15)` : "transparent",
              border: `1.5px solid ${stageColor}`,
              borderRadius: "6px",
              color: stageColor,
              fontWeight: 700,
              fontSize: "13px",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.6 : 1,
              boxShadow: hoveredSubmit && !submitting
                ? `0 0 20px rgba(${stageColorRgb}, 0.4), 0 0 60px rgba(${stageColorRgb}, 0.15)`
                : "none",
              transition: "box-shadow 0.3s ease, opacity 0.2s ease, background 0.2s ease",
            }}
          >
            {submitting ? "Creating Account..." : "Create Account"}
          </button>
        </form>

        <p style={{
          textAlign: "center",
          fontSize: "12px",
          color: "#606070",
          marginTop: "20px",
          marginBottom: 0,
        }}>
          Already have an account?{" "}
          <Link to="/login" style={{ color: stageColor, textDecoration: "none", fontWeight: 600 }}>
            Log in
          </Link>
        </p>
      </div>
    </div>
  );
}
